import { Loader2, Wifi, WifiOff, Users, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useHasPermission } from '@/lib/hooks/usePermission'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { Button } from '@/components/ui/button'

import type { BobAnalyse } from '@/lib/services/bobAnalyseService'
import { bobAnalyseService } from '@/lib/services/bobAnalyseService'
import { EditableBox } from './EditableBox'
import { AddSectionDialog } from './AddSectionDialog'
import { websocketService } from '@/lib/services/websocketService'
import { cn } from '@/lib/utils'
import { useToast } from '@/lib/hooks/useToast'

interface ActiveUser {
  name: string
  email: string
  avatar?: string
  sid: string
}

interface BobAnalyseCardProps {
  bobAnalyses: BobAnalyse[]
  onRefetch: () => void
  selectedBobId: string
  onExportReady?: (exportFn: () => Promise<void>) => void
}

export function BobAnalyseCard({
  bobAnalyses,
  onRefetch,
  selectedBobId,
  onExportReady,
}: BobAnalyseCardProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const selectedBobAnalyse = bobAnalyses.find(bob => bob.id === selectedBobId)
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentBobAnalyseId, setCurrentBobAnalyseId] = useState<string | undefined>(
    selectedBobAnalyse?.id
  )
  const [forceRemount, setForceRemount] = useState<number>(0)
  const beeldvormingBoxRefs = useRef<Record<string, { save: () => Promise<void> }>>({})
  const oordeelsvormingBoxRefs = useRef<Record<string, { save: () => Promise<void> }>>({})
  const besluitvormingBoxRefs = useRef<Record<string, { save: () => Promise<void> }>>({})

  // Dialog state for adding new sections
  const [addSectionDialogOpen, setAddSectionDialogOpen] = useState(false)
  const [addSectionTarget, setAddSectionTarget] = useState<
    'beeldvorming' | 'oordeelsvorming' | 'besluitvorming' | null
  >(null)

  // WebSocket connection and presence state
  const [isConnected, setIsConnected] = useState(false)
  const [activeUsers, setActiveUsers] = useState<Record<string, ActiveUser>>({})

  // Permission and crisis checks
  const { activeCrisis } = useCrisisStore()
  const canUpdateBob = useHasPermission('bob.update')
  const isClosedCrisis = activeCrisis?.status === 'closed'

  // ============================================================================
  // WebSocket Connection Management
  // ============================================================================

  useEffect(() => {
    if (!selectedBobId) return

    let mounted = true

    const initWebSocket = async () => {
      try {
        await websocketService.connect()
        if (mounted) {
          websocketService.joinBobAnalysis(selectedBobId)
        }
      } catch (error) {
        console.error('[BobAnalyseCard] WebSocket connection failed:', error)
      }
    }

    initWebSocket()

    return () => {
      mounted = false
      websocketService.leaveBobAnalysis()
    }
  }, [selectedBobId])

  // Subscribe to connection status changes
  useEffect(() => {
    const unsubscribe = websocketService.onConnectionStatus(connected => {
      setIsConnected(connected)
    })
    return () => unsubscribe()
  }, [])

  // Subscribe to active users updates
  useEffect(() => {
    const unsubscribe = websocketService.onActiveUsers(users => {
      setActiveUsers(users)
    })
    return () => unsubscribe()
  }, [])

  // Subscribe to content updates - when a category is added/deleted, refetch
  useEffect(() => {
    let refetchTimeout: NodeJS.Timeout | null = null

    const unsubscribe = websocketService.onContentUpdate(() => {
      // Debounce refetch to avoid multiple rapid fetches
      if (refetchTimeout) {
        clearTimeout(refetchTimeout)
      }
      refetchTimeout = setTimeout(() => {
        console.log('[BobAnalyseCard] WebSocket triggered refetch')
        onRefetch()
      }, 300)
    })

    return () => {
      unsubscribe()
      if (refetchTimeout) {
        clearTimeout(refetchTimeout)
      }
    }
  }, [onRefetch])

  useEffect(() => {
    if (selectedBobAnalyse && currentBobAnalyseId !== selectedBobAnalyse.id) {
      setCurrentBobAnalyseId(selectedBobAnalyse.id)
      // Clear any in-progress updates

      // Force a remount of all EditableBox components
      setForceRemount(prev => prev + 1)
    }
  }, [selectedBobAnalyse, currentBobAnalyseId])

  // Create memoized export function
  const handleExport = useCallback(async () => {
    if (!selectedBobAnalyse) return
    const { exportBobAnalyseToWord } = await import('@/lib/utils')
    await exportBobAnalyseToWord(selectedBobAnalyse)
  }, [selectedBobAnalyse])

  // Expose export function to parent component
  useEffect(() => {
    if (onExportReady) {
      onExportReady(handleExport)
    }
  }, [handleExport, onExportReady])

  // Register an EditableBox with our refs
  const registerBox = (
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    category: string,
    saveFunction: () => Promise<void>
  ) => {
    if (section === 'beeldvorming') {
      beeldvormingBoxRefs.current[category] = { save: saveFunction }
    } else if (section === 'oordeelsvorming') {
      oordeelsvormingBoxRefs.current[category] = { save: saveFunction }
    } else {
      besluitvormingBoxRefs.current[category] = { save: saveFunction }
    }
  }

  // Update BOB content mutation
  // IMPORTANT: No onRefetch() call here - WebSocket handles real-time sync
  // Database save is fire-and-forget, WebSocket already broadcast the change
  const updateContentMutation = useMutation({
    mutationFn: ({
      section,
      category,
      values,
    }: {
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming'
      category: string
      values: string[]
    }) => {
      if (!selectedBobAnalyse) throw new Error('No BOB analyse selected')

      return bobAnalyseService.updateBobAnalyseContent(
        selectedBobAnalyse.id,
        section,
        category,
        values
      )
    },
    // No onSuccess with onRefetch() - WebSocket is the source of truth
    onError: () => {
      console.error('Error updating BOB analysis')
    },
  })

  // Helper function to save BOB content - wrapped in useCallback for stability
  const handleSaveContent = useCallback(
    async (
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
      category: string,
      newValues: string[]
    ): Promise<void> => {
      // Check permissions before saving
      if (!canUpdateBob || isClosedCrisis) {
        console.debug('[BobAnalyseCard] Save blocked: no permission or crisis closed')
        return
      }

      console.debug('[BobAnalyseCard] Saving content:', { section, category, values: newValues })

      try {
        await updateContentMutation.mutateAsync({
          section,
          category,
          values: newValues,
        })
        console.debug('[BobAnalyseCard] Save successful')
      } catch (error) {
        console.error('[BobAnalyseCard] Save failed:', error)
        throw error // Re-throw so EditableBox knows it failed
      }
    },
    [canUpdateBob, isClosedCrisis, updateContentMutation]
  )

  // Add category mutation
  const addCategoryMutation = useMutation({
    mutationFn: ({
      section,
      categoryName,
    }: {
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming'
      categoryName: string
    }) => {
      if (!selectedBobAnalyse) throw new Error('No BOB analyse selected')
      return bobAnalyseService.addCategory(selectedBobAnalyse.id, section, categoryName)
    },
    onSuccess: (_, variables) => {
      // Broadcast category addition via WebSocket
      websocketService.broadcastCategoryAdded(variables.section, variables.categoryName)

      toast({
        title: 'Sectie toegevoegd',
        description: 'De nieuwe sectie is succesvol toegevoegd.',
      })
      onRefetch()
    },
    onError: error => {
      console.error('Error adding category:', error)
      toast({
        title: 'Fout bij toevoegen',
        description: 'Er is een fout opgetreden bij het toevoegen van de sectie.',
        variant: 'destructive',
      })
    },
  })

  // Delete category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: ({
      section,
      categoryName,
      currentContent,
    }: {
      section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming'
      categoryName: string
      currentContent: Record<string, string[]>
    }) => {
      if (!selectedBobAnalyse) throw new Error('No BOB analyse selected')
      return bobAnalyseService.deleteCategory(
        selectedBobAnalyse.id,
        section,
        currentContent,
        categoryName
      )
    },
    onSuccess: (_, variables) => {
      // Broadcast category deletion via WebSocket
      websocketService.broadcastCategoryDeleted(variables.section, variables.categoryName)

      toast({
        title: 'Sectie verwijderd',
        description: 'De sectie is succesvol verwijderd.',

      })
      onRefetch()
    },
    onError: error => {
      console.error('Error deleting category:', error)
      toast({
        title: 'Fout bij verwijderen',
        description: 'Er is een fout opgetreden bij het verwijderen van de sectie.',
        variant: 'destructive',
      })
    },
  })

  // Handle opening add section dialog
  const handleOpenAddSection = (section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming') => {
    setAddSectionTarget(section)
    setAddSectionDialogOpen(true)
  }

  // Handle confirming new section
  const handleConfirmAddSection = async (categoryName: string) => {
    if (!addSectionTarget || !selectedBobAnalyse) return

    await addCategoryMutation.mutateAsync({
      section: addSectionTarget,
      categoryName,
    })
  }

  // Handle deleting a section
  const handleDeleteSection = async (
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    categoryName: string
  ) => {
    if (!selectedBobAnalyse) {
      console.error('[BobAnalyseCard] No selected analyse for deletion')
      return
    }

    const currentContent = selectedBobAnalyse[section] as Record<string, string[]> | null
    if (!currentContent) {
      console.error('[BobAnalyseCard] No content found for section:', section)
      return
    }

    console.log('[BobAnalyseCard] Deleting category:', { section, categoryName })

    try {
      await deleteCategoryMutation.mutateAsync({
        section,
        categoryName,
        currentContent,
      })
      console.log('[BobAnalyseCard] Category deleted successfully')
    } catch (error) {
      console.error('[BobAnalyseCard] Failed to delete category:', error)
      throw error // Re-throw so EditableBox can handle it
    }
  }

  // Helper to determine if a category is custom (not in the default set)
  const isCustomCategory = (
    section: 'beeldvorming' | 'oordeelsvorming' | 'besluitvorming',
    category: string
  ): boolean => {
    const defaultCategories = {
      beeldvorming: ['oorzaak', 'gebeurtenis', 'gevolg', 'maatregel', 'effect', 'partners'],
      oordeelsvorming: [
        'getroffen cirkel',
        'doel(en)',
        'doelen',
        'strategie (ibs)',
        'scenario',
        'scenarios',
        "scenario's",
        'middelen',
        'kernboodschappen',
      ],
      besluitvorming: ['acties', 'door', 'vergaderklok(ken)', 'vragen?'],
    }

    return !defaultCategories[section].includes(category.toLowerCase())
  }

  const createBobAnalyseMutation = useMutation({
    mutationFn: (scanId: string) => bobAnalyseService.createBobAnalyse({ scanId }),
    onSuccess: async response => {
      // Set local processing state to true
      setIsProcessing(true)

      // Poll for the created BOB analysis without toast notifications
      const pollForResult = async () => {
        try {
          const bobAnalyse = await bobAnalyseService.getBobAnalyse(response.id)
          if (bobAnalyse.status === 'completed' || bobAnalyse.status === 'failed') {
            onRefetch()
            // Keep processing state for a moment to avoid flickering
            setTimeout(() => setIsProcessing(false), 500)
          } else {
            // Continue polling
            setTimeout(pollForResult, 2000)
          }
        } catch (error) {
          console.error('Error polling BOB analysis:', error)
          setTimeout(pollForResult, 2000)
        }
      }

      pollForResult()
    },
    onError: error => {
      console.error('Error creating BOB analysis:', error)
      onRefetch() // Refresh to show error state
      setIsProcessing(false)
    },
  })

  return (
    <>
      <div className="space-y-8">
        {/* Connection status and active users indicator */}
        {selectedBobAnalyse && selectedBobAnalyse.status === 'completed' && !isProcessing && (
          <div className="flex items-center justify-between px-4 py-2 bg-white border border-gray-200 rounded-lg">
            {/* Connection status */}
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <Wifi className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-700">Verbonden</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-700">Niet verbonden</span>
                </>
              )}
            </div>

            {/* Active users count */}
            {Object.keys(activeUsers).length > 0 && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-600">
                  {Object.keys(activeUsers).length} gebruiker{Object.keys(activeUsers).length !== 1 ? 's' : ''} actief
                </span>
                {/* Show user names as tooltip-style list */}
                <div className="flex -space-x-2">
                  {Object.values(activeUsers)
                    .slice(0, 3)
                    .map((user, idx) => (
                      <div
                        key={idx}
                        className={cn(
                          'w-7 h-7 rounded-full bg-blue-100 border-2 border-white',
                          'flex items-center justify-center text-xs font-medium text-blue-700'
                        )}
                        title={user.name}
                      >
                        {user.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    ))}
                  {Object.keys(activeUsers).length > 3 && (
                    <div className="w-7 h-7 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                      +{Object.keys(activeUsers).length - 3}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Processing state - show for draft, processing, or when mutations are pending */}
        {(isProcessing ||
          createBobAnalyseMutation.isPending ||
          (selectedBobAnalyse &&
            (selectedBobAnalyse.status === 'processing' ||
              selectedBobAnalyse.status === 'draft' ||
              selectedBobAnalyse.status === 'queued'))) && (
          <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg shadow border border-blue-100">
            <div className="inline-flex items-center justify-center mb-4 h-12 px-8 rounded-md text-base font-medium border bg-blue-50 border-blue-200 text-blue-700">
              <Loader2 className="mr-3 h-5 w-5 animate-spin" />
              BOB-GOGME analyse wordt gegenereerd...
            </div>
            <span className="text-sm text-muted-foreground">Dit kan enkele minuten duren</span>
          </div>
        )}

        {/* Failed state */}
        {selectedBobAnalyse && selectedBobAnalyse.status === 'failed' && (
          <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg shadow border border-red-100">
            <div className="inline-flex items-center justify-center mb-4 h-12 px-8 rounded-md text-base font-medium border bg-red-50 border-red-200 text-red-700">
              BOB-GOGME analyse generatie mislukt
            </div>
            <span className="text-sm text-muted-foreground">Probeer het opnieuw te genereren</span>
          </div>
        )}

        {/* Show BOB analyse content when selected and status is completed */}
        {selectedBobAnalyse && selectedBobAnalyse.status === 'completed' && !isProcessing && (
          <>
            {/* Beeldvorming - Big standalone box with centered header */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-md">
              <div className="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-orange-500">
                  {t('bob.beeldvorming')}
                </h2>
                {canUpdateBob && !isClosedCrisis && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenAddSection('beeldvorming')}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Voeg sectie toe
                  </Button>
                )}
              </div>
              <div className="p-6">
                {selectedBobAnalyse.beeldvorming ? (
                  <>
                    {(() => {
                      // Map of preferred positions for specific categories
                      const positionMap: Record<string, number> = {
                        oorzaak: 0, // Top left
                        gebeurtenis: 1, // Top center
                        gevolg: 2, // Top right
                        maatregel: 3, // Bottom left
                        effect: 4, // Bottom center
                        partners: 5, // Bottom right
                      }

                      // Extract all categories
                      const beeldvorming = selectedBobAnalyse.beeldvorming as Record<
                        string,
                        string[]
                      >
                      const categories = Object.keys(beeldvorming)

                      // Sort categories by position (defined ones first, then preserve insertion order for custom)
                      const sortedCategories = [...categories].sort((a, b) => {
                        const keyA = a.toLowerCase()
                        const keyB = b.toLowerCase()
                        const posA = keyA in positionMap ? positionMap[keyA] : 999
                        const posB = keyB in positionMap ? positionMap[keyB] : 999
                        if (posA === posB) return categories.indexOf(a) - categories.indexOf(b)
                        return posA - posB
                      })

                      return (
                        <div className="grid grid-cols-3 gap-4 auto-rows-fr">
                          {sortedCategories.map(category => {
                            const values = beeldvorming[category] || []
                            const isCustom = isCustomCategory('beeldvorming', category)

                            return (
                              <EditableBox
                                key={`${selectedBobAnalyse.id}-beeldvorming-${category}-${forceRemount}`}
                                title={category}
                                values={values}
                                colorScheme="neutral"
                                section="beeldvorming"
                                category={category}
                                registerSave={saveFunction =>
                                  registerBox('beeldvorming', category, saveFunction)
                                }
                                onSave={newValues =>
                                  handleSaveContent('beeldvorming', category, newValues)
                                }
                                isCustom={isCustom}
                                {...(isCustom && {
                                  onDelete: () => handleDeleteSection('beeldvorming', category),
                                })}
                              />
                            )
                          })}
                        </div>
                      )
                    })()}
                  </>
                ) : (
                  <p className="text-muted-foreground italic text-sm">
                    Geen beeldvorming beschikbaar
                  </p>
                )}
              </div>
            </div>

            {/* Oordeelsvorming - Big standalone box with centered header */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-md">
              <div className="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-orange-500">
                  {t('bob.oordeelsvorming')}
                </h2>
                {canUpdateBob && !isClosedCrisis && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenAddSection('oordeelsvorming')}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Voeg sectie toe
                  </Button>
                )}
              </div>
              <div className="p-6">
                {selectedBobAnalyse.oordeelsvorming ? (
                  <>
                    {(() => {
                      // Map of preferred positions for specific categories
                      const positionMap: Record<string, number> = {
                        'getroffen cirkel': 0,
                        'doel(en)': 1,
                        doelen: 1,
                        'strategie (ibs)': 2,
                        scenario: 3,
                        scenarios: 3,
                        "scenario's": 3,
                        middelen: 4,
                        kernboodschappen: 5,
                      }

                      // Extract all categories
                      const oordeelsvorming = selectedBobAnalyse.oordeelsvorming as Record<
                        string,
                        string[]
                      >
                      const categories = Object.keys(oordeelsvorming)

                      // Sort categories by position (defined ones first, then preserve insertion order for custom)
                      const sortedCategories = [...categories].sort((a, b) => {
                        const keyA = a.toLowerCase()
                        const keyB = b.toLowerCase()
                        const posA = keyA in positionMap ? positionMap[keyA] : 999
                        const posB = keyB in positionMap ? positionMap[keyB] : 999
                        if (posA === posB) return categories.indexOf(a) - categories.indexOf(b)
                        return posA - posB
                      })

                      return (
                        <div className="grid grid-cols-3 gap-4 auto-rows-fr">
                          {sortedCategories.map(category => {
                            const values = oordeelsvorming[category] || []
                            const isCustom = isCustomCategory('oordeelsvorming', category)

                            return (
                              <EditableBox
                                key={`${selectedBobAnalyse.id}-oordeelsvorming-${category}-${forceRemount}`}
                                title={category}
                                values={values}
                                colorScheme="neutral"
                                section="oordeelsvorming"
                                category={category}
                                registerSave={saveFunction =>
                                  registerBox('oordeelsvorming', category, saveFunction)
                                }
                                onSave={newValues =>
                                  handleSaveContent('oordeelsvorming', category, newValues)
                                }
                                isCustom={isCustom}
                                {...(isCustom && {
                                  onDelete: () => handleDeleteSection('oordeelsvorming', category),
                                })}
                              />
                            )
                          })}
                        </div>
                      )
                    })()}
                  </>
                ) : (
                  <p className="text-muted-foreground italic text-sm">
                    Geen oordeelsvorming beschikbaar
                  </p>
                )}
              </div>
            </div>

            {/* Besluitvorming - Big standalone box with centered header */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-md">
              <div className="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-orange-500">Besluitvorming</h2>
                {canUpdateBob && !isClosedCrisis && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenAddSection('besluitvorming')}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Voeg sectie toe
                  </Button>
                )}
              </div>
              <div className="p-6">
                {selectedBobAnalyse.besluitvorming ? (
                  <>
                    {(() => {
                      const besluitvorming = selectedBobAnalyse.besluitvorming as Record<
                        string,
                        string[]
                      >
                      const categories = Object.keys(besluitvorming)

                      // Sort categories: Acties, Door, then others in insertion order
                      const sortedCategories = [...categories].sort((a, b) => {
                        const order = ['acties', 'door', 'vergaderklok(ken)', 'vragen?']
                        const aLower = a.toLowerCase()
                        const bLower = b.toLowerCase()
                        const aIndex = order.indexOf(aLower)
                        const bIndex = order.indexOf(bLower)

                        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
                        if (aIndex !== -1) return -1
                        if (bIndex !== -1) return 1
                        return categories.indexOf(a) - categories.indexOf(b)
                      })

                      return (
                        <div className="grid grid-cols-3 gap-4 auto-rows-fr">
                          {sortedCategories.map(category => {
                            const values = besluitvorming[category] || []
                            const isCustom = isCustomCategory('besluitvorming', category)

                            return (
                              <EditableBox
                                key={`${selectedBobAnalyse.id}-besluitvorming-${category}-${forceRemount}`}
                                title={category}
                                values={values}
                                colorScheme="neutral"
                                section="besluitvorming"
                                category={category}
                                registerSave={saveFunction =>
                                  registerBox('besluitvorming', category, saveFunction)
                                }
                                onSave={newValues =>
                                  handleSaveContent('besluitvorming', category, newValues)
                                }
                                isCustom={isCustom}
                                {...(isCustom && {
                                  onDelete: () => handleDeleteSection('besluitvorming', category),
                                })}
                              />
                            )
                          })}
                        </div>
                      )
                    })()}
                  </>
                ) : (
                  <p className="text-muted-foreground italic text-sm">
                    Geen besluitvorming beschikbaar
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Section Dialog */}
      <AddSectionDialog
        open={addSectionDialogOpen}
        onOpenChange={setAddSectionDialogOpen}
        onConfirm={handleConfirmAddSection}
        sectionName={
          addSectionTarget === 'beeldvorming'
            ? 'Beeldvorming'
            : addSectionTarget === 'oordeelsvorming'
              ? 'Oordeelsvorming'
              : 'Besluitvorming'
        }
        existingCategories={
          selectedBobAnalyse && addSectionTarget
            ? Object.keys(selectedBobAnalyse[addSectionTarget] as Record<string, string[]> || {})
            : []
        }
      />
    </>
  )
}
